import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface DashboardStats {
  totalPatients: number
  activePatients: number
  totalSlides: number
  pendingReports: number
  totalReports: number
  activeDevices: number
  storageUsed: number
  storageTotal: number
  activeSessions: number
}

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation()
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')

  // Format bytes to human readable
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }
  const currentTime = new Date().toLocaleDateString(i18n.language === 'vi' ? 'vi-VN' : 'en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  // Check if user has organization
  const hasOrganization = currentUser.organization_name

  const [stats, setStats] = useState<DashboardStats>({
    totalPatients: 0,
    activePatients: 0,
    totalSlides: 0,
    pendingReports: 0,
    totalReports: 0,
    activeDevices: 0,
    storageUsed: 0,
    storageTotal: 0,
    activeSessions: 0
  })
  const [loading, setLoading] = useState(true)
  const [recentActivity, setRecentActivity] = useState<any[]>([])

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [patientsRes, slidesRes, reportsRes, devicesRes, systemStatsRes] = await Promise.all([
          fetch('/api/v1/pathology/patients/'),
          fetch('/api/v1/imaging/slides/'),
          fetch('/api/v1/pathology/reports/'),
          fetch('/api/v1/devices/devices/'),
          fetch('/api/v1/analytics/system-stats/')
        ])

        const patientsData = await patientsRes.json()
        const slidesData = await slidesRes.json()
        const reportsData = await reportsRes.json()
        const devicesData = await devicesRes.json()
        const systemStats = await systemStatsRes.json()

        // Count active patients
        const activePatientCount = patientsData.results?.filter((p: any) =>
          p.is_active === true
        ).length || 0

        // Count pending reports
        const pendingReportCount = reportsData.results?.filter((r: any) =>
          r.status === 'draft' || r.status === 'pending'
        ).length || 0

        // Count active devices
        const activeDeviceCount = devicesData.results?.filter((d: any) =>
          d.status === 'active' || d.is_active === true
        ).length || 0

        // Get real storage stats from system-stats API
        const storageUsed = systemStats.storage?.used || 0
        const storageTotal = systemStats.storage?.total || (982 * 1024 * 1024 * 1024) // 982GB
        const activeSessions = systemStats.sessions?.active || 0

        setStats({
          totalPatients: patientsData.count || patientsData.results?.length || 0,
          activePatients: activePatientCount,
          totalSlides: slidesData.count || slidesData.results?.length || 0,
          pendingReports: pendingReportCount,
          totalReports: reportsData.count || reportsData.results?.length || 0,
          activeDevices: activeDeviceCount,
          storageUsed: storageUsed,
          storageTotal: storageTotal,
          activeSessions: activeSessions
        })

        // Build recent activity from real data
        const activities: any[] = []

        // Add recent patients
        if (patientsData.results && patientsData.results.length > 0) {
          patientsData.results.slice(0, 2).forEach((patient: any) => {
            activities.push({
              type: 'patient',
              title: 'New patient registered',
              description: `${patient.first_name} ${patient.last_name} - ${patient.patient_id}`,
              time: getTimeAgo(patient.created_at),
              status: 'completed'
            })
          })
        }

        // Add recent slides
        if (slidesData.results && slidesData.results.length > 0) {
          slidesData.results.slice(0, 2).forEach((slide: any) => {
            activities.push({
              type: 'scan',
              title: 'Image uploaded',
              description: `${slide.name || 'Slide'} - ${slide.file_size_mb || 0} MB`,
              time: getTimeAgo(slide.created_at),
              status: 'completed'
            })
          })
        }

        // Add recent reports
        if (reportsData.results && reportsData.results.length > 0) {
          reportsData.results.slice(0, 1).forEach((report: any) => {
            activities.push({
              type: 'report',
              title: 'Report created',
              description: `Report for patient`,
              time: getTimeAgo(report.created_at),
              status: report.status === 'draft' ? 'pending' : 'completed'
            })
          })
        }

        // Sort by most recent
        activities.sort((a, b) => {
          const timeA = parseTimeAgo(a.time)
          const timeB = parseTimeAgo(b.time)
          return timeA - timeB
        })

        setRecentActivity(activities.slice(0, 5))

      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  // Helper to calculate time ago
  const getTimeAgo = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (seconds < 60) return `${seconds} sec ago`
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) > 1 ? 's' : ''} ago`
    return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) > 1 ? 's' : ''} ago`
  }

  const parseTimeAgo = (timeStr: string): number => {
    const match = timeStr.match(/(\d+)\s+(sec|min|hour|day)/)
    if (!match) return 999999
    const value = parseInt(match[1])
    const unit = match[2]
    const multipliers: any = { sec: 1, min: 60, hour: 3600, day: 86400 }
    return value * (multipliers[unit] || 1)
  }

  const statsDisplay = [
    {
      name: t('dashboard.totalPatients'),
      value: loading ? '-' : stats.totalPatients.toLocaleString(),
      change: '',
      changeType: 'neutral',
      icon: '👥',
      description: `${stats.activePatients} ${t('dashboard.activePatients')}`
    },
    {
      name: t('dashboard.totalReports'),
      value: loading ? '-' : stats.totalReports.toLocaleString(),
      change: '',
      changeType: 'neutral',
      icon: '📋',
      description: `${stats.pendingReports} ${t('dashboard.pendingReports')}`
    },
    {
      name: t('dashboard.imagesProcessed'),
      value: loading ? '-' : stats.totalSlides.toLocaleString(),
      change: '',
      changeType: 'neutral',
      icon: '🖼️',
      description: t('dashboard.digitalSlides')
    },
    {
      name: t('dashboard.activeDevices'),
      value: loading ? '-' : stats.activeDevices.toLocaleString(),
      change: '',
      changeType: 'neutral',
      icon: '🔧',
      description: t('dashboard.onlineDevices')
    },
  ]


  const systemStatus = [
    { service: 'Database Server', status: 'online', uptime: '99.9%', icon: '🗄️' },
    { service: 'Image Storage', status: 'online', uptime: '99.8%', icon: '💾' },
    { service: 'API Gateway', status: 'online', uptime: '100%', icon: '🚪' },
    { service: 'Processing Queue', status: 'online', uptime: '99.5%', icon: '⚡' },
  ]

  const quickActions = [
    { name: t('dashboard.newPatient'), href: '/pathology/patients', icon: '➕', color: 'bg-blue-600 hover:bg-blue-700' },
    { name: t('dashboard.viewImages'), href: '/imaging/library', icon: '👁️', color: 'bg-green-600 hover:bg-green-700' },
    { name: t('dashboard.generateReport'), href: '/pathology/reports', icon: '📋', color: 'bg-purple-600 hover:bg-purple-700' },
    { name: t('dashboard.viewReports'), href: '/analytics/reports', icon: '📊', color: 'bg-indigo-600 hover:bg-indigo-700' },
  ]

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">{t('dashboard.welcomeBack')}, {currentUser.name}! 👋</h1>
            <p className="text-blue-100">{currentTime}</p>
            {hasOrganization && !currentUser.is_staff && (
              <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full bg-blue-700 text-blue-100">
                <span className="mr-2">🏥</span>
                <span className="text-sm font-medium">{currentUser.organization_name}</span>
              </div>
            )}
            {currentUser.is_staff && (
              <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full bg-yellow-500 text-yellow-900">
                <span className="mr-2">👑</span>
                <span className="text-sm font-medium">{t('dashboard.adminAllOrgs')}</span>
              </div>
            )}
          </div>
          <div className="text-right">
            <p className="text-blue-100 text-sm">{t('dashboard.systemStatus')}</p>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="font-medium">{t('dashboard.allSystemsOperational')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {quickActions.map((action) => (
          <Link
            key={action.name}
            to={action.href}
            className={`${action.color} text-white p-4 rounded-lg text-center transition-colors group`}
          >
            <div className="text-2xl mb-2">{action.icon}</div>
            <div className="text-sm font-medium">{action.name}</div>
          </Link>
        ))}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsDisplay.map((stat) => (
          <div key={stat.name} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="text-3xl">{stat.icon}</div>
              {stat.change && (
                <div className={`text-sm font-medium px-2 py-1 rounded-full ${
                  stat.changeType === 'increase'
                    ? 'bg-green-100 text-green-800'
                    : stat.changeType === 'decrease'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                }`}>
                  {stat.change}
                </div>
              )}
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</p>
              <p className="text-sm font-medium text-gray-600 mb-1">{stat.name}</p>
              <p className="text-xs text-gray-500">{stat.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.recentActivity')}</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <div key={index} className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    activity.status === 'completed' ? 'bg-green-400' :
                    activity.status === 'warning' ? 'bg-yellow-400' : 'bg-blue-400'
                  }`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-600 truncate">{activity.description}</p>
                    <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    activity.status === 'completed' ? 'bg-green-100 text-green-800' :
                    activity.status === 'warning' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {activity.status}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Link to="/analytics/audit" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                {t('dashboard.viewAllActivity')} →
              </Link>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.systemHealth')}</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {systemStatus.map((service, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">{service.icon}</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{service.service}</p>
                      <p className="text-xs text-gray-500">{t('dashboard.uptime')}: {service.uptime}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {t('dashboard.online')}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600 space-y-2">
                <div className="flex justify-between">
                  <span>Storage Used:</span>
                  <span className="font-medium">
                    {loading ? 'Loading...' : `${formatBytes(stats.storageUsed)} / ${formatBytes(stats.storageTotal)}`}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${stats.storageTotal > 0 ? Math.min((stats.storageUsed / stats.storageTotal) * 100, 100) : 0}%` }}
                  ></div>
                </div>
                <div className="flex justify-between">
                  <span>Active Sessions:</span>
                  <span className="font-medium">
                    {loading ? 'Loading...' : `${stats.activeSessions} ${stats.activeSessions === 1 ? 'user' : 'users'}`}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard